import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import { getAdmins, createAdmin } from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";

export default function CreateClientDialog({ open, onClose, onSnackbar }) {
  const { t } = useTranslation();
  const [clientType, setClientType] = useState(0);
  const [formData, setFormData] = useState({
    clientName: "",
    vkn_tckn: "",
    address: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [existingClients, setExistingClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");

  const handleOpen = async () => {
    try {
      const clients = await getAdmins();
      setExistingClients(clients || []);
    } catch (err) {
      console.error("Error fetching clients:", err);
      setExistingClients([]);
    }
  };

  React.useEffect(() => {
    if (open) {
      setClientType(0);
      setFormData({ clientName: "", vkn_tckn: "", address: "" });
      setFormErrors({});
      setSelectedClient("");
      handleOpen();
    }
  }, [open]);

  const handleClose = () => {
    setFormData({ clientName: "", vkn_tckn: "", address: "" });
    setFormErrors({});
    onClose();
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setClientType(newType);
      setFormData({ clientName: "", vkn_tckn: "", address: "" });
      setFormErrors({});
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.clientName.trim()) {
      errors.clientName = "Organizasyon adı zorunludur";
    }
    if (clientType === 0) {
      if (!formData.vkn_tckn.trim()) {
        errors.vkn_tckn = "Vergi Kimlik Numarası zorunludur";
      }
      if (!formData.address.trim()) {
        errors.address = "Adres zorunludur";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const payload = {
        clientName: formData.clientName,
        vkn_tckn: clientType === 0 ? formData.vkn_tckn : "",
        address: formData.address,
        type: clientType,
      };
      const response = await createAdmin(payload);
      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Organizasyon oluşturma başarısız");
      }
      onSnackbar({
        open: true,
        message: "Organizasyon başarıyla oluşturuldu",
        severity: "success",
      });
      handleClose();
    } catch (error) {
      console.error("Error creating client:", error);
      setFormErrors({
        submit: error.message || "Organizasyon oluşturulurken hata oluştu",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Organizasyon Seçin veya Oluşturun</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Mevcut organizasyon'larınızdan birini seçin:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Mevcut Organizasyon'lar</InputLabel>
            <Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              label="Mevcut Organizasyon'lar"
            >
              {existingClients.map((client) => (
                <MenuItem
                  key={client.id || client.clientName}
                  value={client.id || client.clientName}
                >
                  {client.clientName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }}>veya</Divider>

          <Typography variant="subtitle1" fontWeight="bold">
            Yeni bir organizasyon oluşturun:
          </Typography>

          <ToggleButtonGroup
            value={clientType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            color="primary"
          >
            <ToggleButton value={0} sx={{ fontWeight: "bold" }}>
              Kurumsal
            </ToggleButton>
            <ToggleButton value={1} sx={{ fontWeight: "bold" }}>
              Bireysel
            </ToggleButton>
          </ToggleButtonGroup>

          {formErrors.submit && (
            <Typography color="error" variant="body2">
              {formErrors.submit}
            </Typography>
          )}

          <TextField
            label="Organizasyon Adı"
            value={formData.clientName}
            onChange={(e) => handleFormChange("clientName", e.target.value)}
            fullWidth
            required
            error={Boolean(formErrors.clientName)}
            helperText={formErrors.clientName}
          />

          {clientType === 0 && (
            <TextField
              label="Vergi Kimlik Numarası"
              value={formData.vkn_tckn}
              onChange={(e) => handleFormChange("vkn_tckn", e.target.value)}
              fullWidth
              required
              error={Boolean(formErrors.vkn_tckn)}
              helperText={formErrors.vkn_tckn}
            />
          )}

          <TextField
            label="Adres"
            value={formData.address}
            onChange={(e) => handleFormChange("address", e.target.value)}
            fullWidth
            required={clientType === 0}
            error={Boolean(formErrors.address)}
            helperText={formErrors.address}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
